import { useRouter } from "next/router";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useActiveClassroom } from "@sol/classrooms";
import { Feature, useCheckFeatureFlag } from "@sol/features";
import { interpolate, Route } from "@sol/routing";
import { FileType, getResourceUuid, ImportType, useClassroomImportLearnerMutation } from "@sol/sdk";
import { InformationCircleContained } from "@sol/uikit/core/icons/General/InformationCircleContained";
import { message } from "@sol/uikit/feedback/Message/Message";
import { Modal } from "@sol/uikit/feedback/Modal/Modal";
import { Button } from "@sol/uikit/general/Button/Button";

import { ClassroomLearnersFormValues } from "./ClassroomLearners";
import ApiErrorAlert from "../../../components/ApiErrorAlert";

type Props = {
    isVisible: boolean;
    onCancel: () => void;
};

export const ClassroomLearnersFileImportModal = ({ isVisible, onCancel }: Props) => {
    const [t] = useTranslation();

    const { activeClassroom, refetch: refetchClassroom } = useActiveClassroom();
    const { watch } = useFormContext<ClassroomLearnersFormValues>();
    const { push } = useRouter();
    const [messageApi, contextHolder] = message.useMessage();

    const checkFeatureFlag = useCheckFeatureFlag();

    const file = watch("file");

    const {
        mutate: importFile,
        isPending,
        error,
        reset,
    } = useClassroomImportLearnerMutation({
        onSuccess: () => {
            reset();
            onCancel();
            refetchClassroom();
            messageApi.success(t("page.home.confirmMessages.updateLearners"));

            if (activeClassroom && checkFeatureFlag(Feature.HAS_ACTIVE_CLASSROOM_CTX)) {
                push(interpolate(Route.CLASSROOM_DASHBOARD, { classroomId: getResourceUuid(activeClassroom) }));
            } else {
                push(Route.HOME);
            }
        },
    });

    const handleReplace = () => {
        if (!activeClassroom) {
            return;
        }

        importFile({
            classroom: activeClassroom,
            importType: ImportType.REPLACE,
            fileType: FileType.CSV,
            file: file as File,
        });
    };
    const handleAppend = () => {
        if (!activeClassroom) {
            return;
        }

        importFile({
            classroom: activeClassroom,
            importType: ImportType.ADD,
            fileType: FileType.CSV,
            file: file as File,
        });
    };
    const handleCancel = onCancel;

    return (
        <>
            <Modal
                title={
                    <span className="flex shrink-0 items-start gap-ant-sm pr-ant-sm">
                        <InformationCircleContained
                            className="inline-block size-[1.5em] text-color-ant-info"
                            variant="antd"
                        />
                        <span className="flex-1">
                            {t("component.ClassroomLearnersFileImportModal.title", {
                                classroom: activeClassroom?.title,
                            })}
                        </span>
                    </span>
                }
                open={isVisible}
                onCancel={handleCancel}
                footer={[
                    <Button key="cancel" onClick={handleCancel} danger>
                        {t("component.ClassroomLearnersFileImportModal.cancel")}
                    </Button>,
                    <Button key="append" onClick={handleAppend} loading={isPending} className="ml-auto">
                        {t("component.ClassroomLearnersFileImportModal.append")}
                    </Button>,
                    <Button key="replace" onClick={handleReplace} type="primary" loading={isPending}>
                        {t("component.ClassroomLearnersFileImportModal.replace")}
                    </Button>,
                ]}
            ></Modal>
            <ApiErrorAlert
                error={error}
                dissmiss={() => {
                    reset();
                    onCancel();
                }}
            />
            {contextHolder}
        </>
    );
};
